import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Pagamentos.css';
import { 
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import { 
    FaArrowLeft, FaChartPie, FaFilter, FaPlus, FaTimes, FaChartArea, 
    FaEdit, FaTrash, FaSync, FaGasPump 
} from 'react-icons/fa';

const formatCurrency = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);
const formatDateChart = (str) => new Date(str).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'UTC' });

// --- TOOLTIP DO GRÁFICO ---
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="custom-tooltip-chart">
                <p className="tooltip-date">{label}</p>
                <p className="tooltip-value">{formatCurrency(payload[0].value)}</p>
                <p className="tooltip-label">Saídas Totais</p>
            </div>
        );
    }
    return null;
};

export default function Pagamentos() {
    const navigate = useNavigate();
    const [pagamentos, setPagamentos] = useState([]);
    const [motoristas, setMotoristas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState(null); // <--- NOVO: Estado do usuário

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [formData, setFormData] = useState({ id: null, valor: '', motorista_id: '', data: new Date().toISOString().split('T')[0], descricao: '' });
    
    const [filtros, setFilters] = useState({ 
        dataInicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        dataFim: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
        descricao: '' 
    });

    // --- EFFECT: CARREGA USUÁRIO LOGADO ---
    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            try {
                setCurrentUser(JSON.parse(storedUser));
            } catch (error) {
                console.error("Erro ao ler usuário:", error);
            }
        }
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const config = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
            const params = new URLSearchParams();
            if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio);
            if (filtros.dataFim) params.append('dataFim', filtros.dataFim);

            const [transacoesRes, motoristasRes] = await Promise.all([
                api.get('/financeiro', { ...config, params }),
                api.get('/api/drivers', config)
            ]);

            const despesasData = (transacoesRes.data || []).filter(t => t.tipo === 'Despesa');
            setPagamentos(despesasData);
            setMotoristas(motoristasRes.data.data || []);
        } catch (error) {
            console.error('Erro ao buscar dados:', error);
        } finally {
            setLoading(false);
        }
    }, [filtros.dataInicio, filtros.dataFim]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const filteredPagamentos = useMemo(() => {
        return pagamentos.filter(p => p.descricao.toLowerCase().includes(filtros.descricao.toLowerCase()));
    }, [pagamentos, filtros.descricao]);

    const resumo = useMemo(() => {
        const total = filteredPagamentos.reduce((acc, p) => p.status !== 'Cancelada' ? acc + (Math.abs(parseFloat(p.valor)) || 0) : acc, 0);
        return { total };
    }, [filteredPagamentos]);

    const chartData = useMemo(() => {
        const daily = {};
        filteredPagamentos.forEach(p => {
            if(p.status === 'Cancelada') return;
            const d = p.data.split('T')[0];
            daily[d] = (daily[d] || 0) + (Math.abs(parseFloat(p.valor)) || 0);
        });
        return Object.keys(daily).sort().map(d => ({ 
            dia: formatDateChart(d), 
            Despesa: daily[d]
        }));
    }, [filteredPagamentos]);

    // --- FUNÇÃO PARA DETECTAR ABASTECIMENTO AUTOMÁTICO ---
    const isAbastecimento = (item) => {
        return item.descricao.includes('Abastecimento Cartão') || 
               (item.categoria_nome && item.categoria_nome.includes('Combust'));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const payload = { 
            ...formData, 
            valor: parseFloat(formData.valor),
            motorista_id: formData.motorista_id || null,
            tipo: 'Despesa', 
            categoria_id: 2, 
            status: 'Aprovada'
        };

        try {
            const config = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
            if(modalMode === 'create') await api.post('/financeiro', payload, config);
            else await api.put(`/financeiro/${formData.id}`, payload, config);
            
            setIsModalOpen(false);
            fetchData();
        } catch (err) { alert('Erro ao salvar pagamento.'); }
    };

    const handleExcluir = async (item) => {
        // --- 1. TRAVA DE ADMIN GERAL ---
        if (!currentUser || currentUser.perfil !== 'admin_geral') {
            alert("🚫 Permissão Negada: Apenas o Admin Geral pode excluir pagamentos.");
            return;
        }

        // --- 2. TRAVA DE INTEGRIDADE (ABASTECIMENTO) ---
        if (isAbastecimento(item)) {
            alert('🔒 Ação Bloqueada: Este é um registro automático de Abastecimento via Cartão.\n\nPara cancelar/estornar, utilize a tela de Gestão de Frota/Cartões para garantir que o saldo do cartão seja devolvido.');
            return;
        }

        if(window.confirm('Excluir pagamento permanentemente?')) {
            try {
                await api.delete(`/financeiro/${item.id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                fetchData();
            } catch (error) { console.error(error); }
        }
    };

    const handleEditClick = (item) => {
        if (isAbastecimento(item)) {
            alert('🔒 Ação Bloqueada: Este registro é automático e não pode ser editado manualmente para manter a integridade com a Frota.');
            return;
        }
        openModal('edit', item);
    };

    const openModal = (mode, data = { id: null, valor: '', motorista_id: '', data: new Date().toISOString().split('T')[0], descricao: '' }) => {
        setModalMode(mode);
        setFormData(mode === 'edit' ? { ...data, data: data.data.split('T')[0], valor: Math.abs(data.valor) } : data);
        setIsModalOpen(true);
    };

    const isAdminGeral = currentUser?.perfil === 'admin_geral';

    return (
        <div className="pagamentos-premium-container">
            <header className="pag-header">
                <div className="pag-header-left">
                    <button onClick={() => navigate(-1)} style={{background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '1.2rem'}}>
                        <FaArrowLeft />
                    </button>
                    <div>
                        <h1>Gestão de Pagamentos</h1>
                        <p>Fluxo de caixa de saída integrado</p>
                    </div>
                </div>
                <button className="btn-danger-action" onClick={() => openModal('create')}>
                    <FaPlus /> Lançamento Rápido
                </button>
            </header>

            <div className="pag-grid-top">
                <div className="kpi-pag-card">
                    <div className="pag-icon-box"><FaChartPie /></div>
                    <div className="pag-info">
                        <span>Total de Saídas</span>
                        <strong>{formatCurrency(resumo.total)}</strong>
                    </div>
                </div>

                <div className="pag-chart-card">
                    <div className="chart-header">
                        <h4><FaChartArea /> Evolução Diária</h4>
                    </div>
                    <div style={{width: '100%', height: 220}}>
                        <ResponsiveContainer>
                            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorDespesa" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#dc3545" stopOpacity={0.8}/>
                                        <stop offset="95%" stopColor="#dc3545" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                <XAxis dataKey="dia" tick={{fontSize: 12, fill: '#9ca3af'}} axisLine={false} tickLine={false} minTickGap={30} />
                                <YAxis tick={{fontSize: 12, fill: '#9ca3af'}} axisLine={false} tickLine={false} />
                                <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#dc3545', strokeWidth: 1, strokeDasharray: '5 5' }} />
                                <Area type="monotone" dataKey="Despesa" stroke="#dc3545" strokeWidth={3} fill="url(#colorDespesa)" activeDot={{ r: 6, strokeWidth: 0, fill: '#dc3545' }} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className="pag-filters">
                <div className="pag-search">
                    <FaFilter />
                    <input type="text" placeholder="Filtrar descrição..." value={filtros.descricao} onChange={e => setFilters({...filtros, descricao: e.target.value})} />
                </div>
                <div className="pag-dates">
                    <input type="date" value={filtros.dataInicio} onChange={e => setFilters({...filtros, dataInicio: e.target.value})} />
                    <input type="date" value={filtros.dataFim} onChange={e => setFilters({...filtros, dataFim: e.target.value})} />
                    <button onClick={fetchData} style={{border: 'none', background: '#dc3545', color: 'white', borderRadius: '8px', width: '40px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <FaSync />
                    </button>
                </div>
            </div>

            <div className="pag-table-container">
                <table className="pag-table">
                    <thead>
                        <tr>
                            <th>Data</th>
                            <th>Descrição</th>
                            <th>Motorista</th>
                            <th>Status</th>
                            <th align="right">Valor</th>
                            <th align="right">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? <tr><td colSpan="6" align="center">Carregando...</td></tr> : filteredPagamentos.map(p => {
                            const automatico = isAbastecimento(p);
                            return (
                                <tr key={p.id}>
                                    <td style={{color: '#64748b'}}>{formatDateChart(p.data)}</td>
                                    <td className="fw-600">
                                        {/* ÍCONE DE COMBUSTÍVEL SE FOR AUTOMÁTICO */}
                                        {automatico && <FaGasPump style={{marginRight: 6, color: '#f59e0b'}} title="Abastecimento Automático" />}
                                        {p.descricao}
                                    </td>
                                    <td style={{color: '#64748b'}}>{motoristas.find(m => m.id === p.motorista_id)?.nome || '-'}</td>
                                    <td><span className={`pag-badge ${p.status}`}>{p.status}</span></td>
                                    <td align="right" className="text-red">{formatCurrency(Math.abs(p.valor))}</td>
                                    <td align="right">
                                        <button 
                                            onClick={() => handleEditClick(p)} 
                                            className={`pag-btn-icon ${automatico ? 'disabled' : ''}`} 
                                            title={automatico ? "Registro automático (bloqueado)" : "Editar"}
                                            style={{ opacity: automatico ? 0.3 : 1, cursor: automatico ? 'not-allowed' : 'pointer' }}
                                        >
                                            <FaEdit />
                                        </button>
                                        <button 
                                            onClick={() => handleExcluir(p)} 
                                            className={`pag-btn-icon delete ${(automatico || !isAdminGeral) ? 'disabled' : ''}`} 
                                            title={automatico ? "Registro automático (bloqueado)" : (!isAdminGeral ? "Apenas Admin Geral pode excluir" : "Excluir")}
                                            style={{ 
                                                opacity: (automatico || !isAdminGeral) ? 0.3 : 1, 
                                                cursor: (automatico || !isAdminGeral) ? 'not-allowed' : 'pointer' 
                                            }}
                                        >
                                            <FaTrash />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Modal - MANTIDO IGUAL */}
            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content-pag">
                        <div className="pag-modal-header">
                            <h3>{modalMode === 'create' ? 'Saída Rápida' : 'Editar Saída'}</h3>
                            <button onClick={() => setIsModalOpen(false)}><FaTimes /></button>
                        </div>
                        <form onSubmit={handleSave}>
                            <div className="pag-modal-body">
                                <label>Valor (R$)</label>
                                <input type="number" step="0.01" value={formData.valor} onChange={e => setFormData({...formData, valor: e.target.value})} required className="input-money-pag" placeholder="0,00" />
                                
                                <label>Descrição</label>
                                <input type="text" value={formData.descricao} onChange={e => setFormData({...formData, descricao: e.target.value})} required placeholder="Ex: Manutenção Rápida" />
                                
                                <div className="row-pag">
                                    <div>
                                        <label>Data</label>
                                        <input type="date" value={formData.data} onChange={e => setFormData({...formData, data: e.target.value})} required />
                                    </div>
                                    <div>
                                        <label>Motorista</label>
                                        <select value={formData.motorista_id} onChange={e => setFormData({...formData, motorista_id: e.target.value})}>
                                            <option value="">Nenhum</option>
                                            {motoristas.map(m => <option key={m.id} value={m.id}>{m.nome}</option>)}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="pag-modal-footer">
                                <button type="button" onClick={() => setIsModalOpen(false)}>Cancelar</button>
                                <button type="submit" className="btn-save-pag">Salvar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}